<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="save">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-1/2 mx-auto md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ $t('Developer options') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/admin/settings">
                            {{ $t('Close') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="w-full xl:w-1/2 mx-auto bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Debug mail routing') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Controls debug delivery for outgoing system emails while regular delivery remains active unless routing is enabled.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-5">
                            <div>
                                <input-switch
                                    id="mail_debug_enabled"
                                    v-model="mail_debug_enabled"
                                    :disabled-label="$t('All emails are sent to their regular recipients')"
                                    :enabled-label="$t('All emails are rerouted to the debug email address')"
                                ></input-switch>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="mail_debug_address">{{ $t('Debug email address') }}</label>
                                <div class="relative">
                                    <input
                                        id="mail_debug_address"
                                        v-model="mail_debug_address"
                                        :disabled="!mail_debug_enabled"
                                        :placeholder="$t('Debug email address')"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        type="email"
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-1/2 mx-auto mt-6 bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Development visibility') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Controls technical notification details and test account visibility in selection lists.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-6">
                            <div>
                                <label class="stage-filter-label block mb-2" for="developer_notification_debug_enabled">{{ $t('Notification developer details') }}</label>
                                <input-switch
                                    id="developer_notification_debug_enabled"
                                    v-model="developer_notification_debug_enabled"
                                    :disabled-label="$t('Request and status are hidden in notifications')"
                                    :enabled-label="$t('Request and status are shown in notifications')"
                                ></input-switch>
                                <div class="mt-2 text-xs text-gray-500 leading-5">
                                    {{ $t('When enabled, notifications include developer details such as request and status. When disabled, users do not see these details.') }}
                                </div>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="developer_hide_test_accounts">{{ $t('Hide test accounts') }}</label>
                                <input-switch
                                    id="developer_hide_test_accounts"
                                    v-model="developer_hide_test_accounts"
                                    :disabled-label="$t('Test accounts remain available in assignment lists')"
                                    :enabled-label="$t('Test accounts are hidden in assignment lists')"
                                ></input-switch>
                                <div class="mt-2 text-xs text-gray-500 leading-5">
                                    {{ $t('When enabled, users marked as test accounts are hidden in selection lists such as select fields, checkbox lists and radio lists. Existing assignments remain visible.') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-1/2 mx-auto mt-6 bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('HTTP Basic Auth') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Adds password protection before the application is loaded by Apache .htaccess.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-5">
                            <div>
                                <input-switch
                                    id="basic_auth_enabled"
                                    v-model="basic_auth_enabled"
                                    :disabled-label="$t('No additional HTTP Basic Auth is active')"
                                    :enabled-label="$t('HTTP Basic Auth is active')"
                                ></input-switch>
                            </div>
                            <div class="md:grid md:grid-cols-2 md:gap-5">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="basic_auth_username">{{ $t('Basic Auth username') }}</label>
                                    <div class="relative">
                                        <input
                                            id="basic_auth_username"
                                            v-model.trim="basic_auth_username"
                                            class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                            maxlength="80"
                                            required
                                            type="text"
                                        >
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0">
                                    <label class="stage-filter-label block mb-2" for="basic_auth_password">{{ $t('Basic Auth password') }}</label>
                                    <div class="relative">
                                        <input
                                            id="basic_auth_password"
                                            v-model="basic_auth_password"
                                            :placeholder="$t('Basic Auth password')"
                                            autocomplete="off"
                                            class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                            maxlength="255"
                                            :required="basic_auth_enabled"
                                            type="text"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-1/2 mx-auto mt-6 bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Environment banner') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t('Serves as a visual differentiator between system environments such as develop, staging or production.') }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-5">
                            <div>
                                <input-switch
                                    id="environment_banner_enabled"
                                    v-model="environment_banner_enabled"
                                    :disabled-label="$t('No environment banner is shown')"
                                    :enabled-label="$t('Display of a banner at the top of the screen')"
                                ></input-switch>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2" for="environment_banner_text">{{ $t('Banner text') }}</label>
                                <div class="relative">
                                    <input
                                        id="environment_banner_text"
                                        v-model.trim="environment_banner_text"
                                        :disabled="!environment_banner_enabled"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        maxlength="120"
                                        :placeholder="$t('Staging environment')"
                                        type="text"
                                    >
                                </div>
                            </div>
                            <input-color
                                id="environment_banner_background_color"
                                v-model="environment_banner_background_color"
                                :label="$t('Background color')"
                                :placeholder="$t('Choose color')"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: "developer-options",
    data() {
        return {
            loading: true,
            mail_debug_enabled: false,
            mail_debug_address: null,
            developer_notification_debug_enabled: false,
            developer_hide_test_accounts: false,
            basic_auth_enabled: false,
            basic_auth_username: 'corpred',
            basic_auth_password: '',
            environment_banner_enabled: false,
            environment_banner_text: '',
            environment_banner_background_color: '#dc2626',
        }
    },
    computed: {
        normalizedEnvironmentBannerColor() {
            const color = String(this.environment_banner_background_color || '').trim();

            return /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(color) ? color : '#dc2626';
        },
    },
    mounted() {
        this.get();
    },
    methods: {
        get() {
            const self = this;
            axios.get('api/workspace/admin/settings/developer-options').then(function (response) {
                self.mail_debug_enabled = !!response.data.mail_debug_enabled;
                self.mail_debug_address = response.data.mail_debug_address;
                self.developer_notification_debug_enabled = !!response.data.developer_notification_debug_enabled;
                self.developer_hide_test_accounts = !!response.data.developer_hide_test_accounts;
                self.basic_auth_enabled = !!response.data.basic_auth_enabled;
                self.basic_auth_username = response.data.basic_auth_username || 'corpred';
                self.basic_auth_password = response.data.basic_auth_password || '';
                self.environment_banner_enabled = !!response.data.environment_banner_enabled;
                self.environment_banner_text = response.data.environment_banner_text || '';
                self.environment_banner_background_color = response.data.environment_banner_background_color || '#dc2626';
                self.loading = false;
            }).catch(function () {
                self.$router.push('/workspace/admin/settings');
            });
        },
        save() {
            const self = this;
            self.loading = true;
            axios.post('api/workspace/admin/settings/developer-options', {
                mail_debug_enabled: !!self.mail_debug_enabled,
                mail_debug_address: self.mail_debug_address,
                developer_notification_debug_enabled: !!self.developer_notification_debug_enabled,
                developer_hide_test_accounts: !!self.developer_hide_test_accounts,
                basic_auth_enabled: !!self.basic_auth_enabled,
                basic_auth_username: self.basic_auth_username || 'corpred',
                basic_auth_password: self.basic_auth_password || '',
                environment_banner_enabled: !!self.environment_banner_enabled,
                environment_banner_text: self.environment_banner_text || '',
                environment_banner_background_color: self.normalizedEnvironmentBannerColor,
            }).then(function () {
                self.loading = false;
                self.$store.commit('patchSettings', {
                    mail_debug_enabled: !!self.mail_debug_enabled,
                    mail_debug_address: self.mail_debug_address,
                    developer_notification_debug_enabled: !!self.developer_notification_debug_enabled,
                    developer_hide_test_accounts: !!self.developer_hide_test_accounts,
                    basic_auth_enabled: !!self.basic_auth_enabled,
                    environment_banner_enabled: !!self.environment_banner_enabled,
                    environment_banner_text: self.environment_banner_text || '',
                    environment_banner_background_color: self.normalizedEnvironmentBannerColor,
                });
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Settings updated successfully').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        }
    }
}
</script>
